import { DEFAULT_GAME_LENGTH, type GameLength } from '@hokm/shared';

type PlayerCount = 2 | 3 | 4;
const DEFAULT_PLAYER_COUNT: PlayerCount = 4;

const STORAGE_KEY_NAME = 'hokm_player_name';
const STORAGE_KEY_SESSION = 'hokm_session';

export interface UrlState {
  room: string | null;
  vanity: string | null;
  name: string | null;
  players: PlayerCount | null;
  length: GameLength | null;
}

export interface JoinConfig {
  roomId: string | null;
  isVanity: boolean;
  playerName: string;
  autoJoin: boolean;
  playerCount: PlayerCount;
  gameLength: GameLength;
}

interface PersistedSession {
  roomId: string;
  playerId: string;
}

export class UrlStateManager {
  /**
   * Parse URL parameters into structured state.
   * Only extracts valid values; invalid values return null.
   */
  static parseUrl(): UrlState {
    const params = new URLSearchParams(window.location.search);

    // Room code: 1-8 alphanumeric chars, uppercase
    const roomRaw = params.get('room');
    const room = roomRaw && /^[A-Z0-9]{1,8}$/i.test(roomRaw) ? roomRaw.toUpperCase() : null;

    // Vanity: 1-32 alphanumeric + hyphen, lowercase
    const vanityRaw = params.get('vanity');
    const vanity = vanityRaw && /^[a-z0-9-]{1,32}$/i.test(vanityRaw) ? vanityRaw.toLowerCase() : null;

    // Name: max 12 chars, trimmed
    const nameRaw = params.get('name');
    const name = nameRaw ? nameRaw.trim().slice(0, 12) : null;

    // Players: 2, 3, or 4
    const playersRaw = params.get('players');
    const playersNum = playersRaw ? parseInt(playersRaw, 10) : null;
    const players = playersNum === 2 || playersNum === 3 || playersNum === 4 ? (playersNum as PlayerCount) : null;

    // Length: 3 or 7
    const lengthRaw = params.get('length');
    const lengthNum = lengthRaw ? parseInt(lengthRaw, 10) : null;
    const length = lengthNum === 3 || lengthNum === 7 ? (lengthNum as GameLength) : null;

    return { room, vanity, name, players, length };
  }

  /**
   * Compute the final join configuration from URL + localStorage.
   */
  static getJoinConfig(): JoinConfig {
    const urlState = this.parseUrl();

    // Priority: URL name > localStorage > default
    const playerName = urlState.name || this.getPersistedName() || 'Player';

    // Room takes precedence over vanity
    const roomId = urlState.room || urlState.vanity;
    const isVanity = !urlState.room && !!urlState.vanity;
    const autoJoin = !!roomId;

    return {
      roomId,
      isVanity,
      playerName,
      autoJoin,
      playerCount: urlState.players || DEFAULT_PLAYER_COUNT,
      gameLength: urlState.length || DEFAULT_GAME_LENGTH,
    };
  }

  /**
   * Update URL parameters using replaceState (no history entry).
   * Only sets non-default, non-null values.
   */
  static updateUrl(params: Partial<UrlState>): void {
    const url = new URL(window.location.href);

    // Room param
    if (params.room !== undefined) {
      if (params.room) {
        url.searchParams.set('room', params.room);
        url.searchParams.delete('vanity'); // Clear vanity when room is set
      } else {
        url.searchParams.delete('room');
      }
    }

    // Vanity param
    if (params.vanity !== undefined) {
      if (params.vanity) {
        url.searchParams.set('vanity', params.vanity);
        url.searchParams.delete('room'); // Clear room when vanity is set
      } else {
        url.searchParams.delete('vanity');
      }
    }

    // Name param (only set if different from localStorage default)
    if (params.name !== undefined) {
      if (params.name && params.name !== 'Player') {
        url.searchParams.set('name', params.name);
      } else {
        url.searchParams.delete('name');
      }
    }

    // Players param (only set if non-default)
    if (params.players !== undefined) {
      if (params.players && params.players !== DEFAULT_PLAYER_COUNT) {
        url.searchParams.set('players', String(params.players));
      } else {
        url.searchParams.delete('players');
      }
    }

    // Length param (only set if non-default)
    if (params.length !== undefined) {
      if (params.length && params.length !== DEFAULT_GAME_LENGTH) {
        url.searchParams.set('length', String(params.length));
      } else {
        url.searchParams.delete('length');
      }
    }

    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Clear room-related params (when leaving a room).
   */
  static clearRoomParams(): void {
    this.updateUrl({ room: null, vanity: null, players: null, length: null });
  }

  /**
   * Generate a shareable link for a room.
   */
  static generateShareLink(roomId: string, isVanity: boolean = false): string {
    const url = new URL(window.location.origin);
    if (isVanity) {
      url.searchParams.set('vanity', roomId.toLowerCase());
    } else {
      url.searchParams.set('room', roomId.toUpperCase());
    }
    return url.toString();
  }

  /**
   * Persist player name to localStorage.
   */
  static persistName(name: string): void {
    if (name && name !== 'Player') {
      try {
        localStorage.setItem(STORAGE_KEY_NAME, name);
      } catch {
        // localStorage may be unavailable
      }
    }
  }

  /**
   * Get persisted name from localStorage.
   */
  static getPersistedName(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY_NAME);
    } catch {
      return null;
    }
  }

  /**
   * Persist session (roomId + playerId) for rejoin on page refresh.
   */
  static persistSession(roomId: string, playerId: string): void {
    try {
      localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify({ roomId, playerId }));
    } catch {
      // localStorage may be unavailable
    }
  }

  /**
   * Get persisted session from localStorage.
   */
  static getPersistedSession(): PersistedSession | null {
    try {
      const data = localStorage.getItem(STORAGE_KEY_SESSION);
      if (!data) return null;
      const session = JSON.parse(data);
      if (session && typeof session.roomId === 'string' && typeof session.playerId === 'string') {
        return session as PersistedSession;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Clear session (when explicitly leaving a room).
   */
  static clearSession(): void {
    try {
      localStorage.removeItem(STORAGE_KEY_SESSION);
    } catch {
      // localStorage may be unavailable
    }
  }
}
